/**
 * IscedFSelect (Task #289)
 *
 * Dropdown for ISCED-F 2013 fagområde. Henter listen fra backend ved første
 * mount (cached i sessionStorage etter første kjøring i sesjonen).
 *
 * Single source of truth = isced_f_codes-tabellen i DB.
 */

import { useEffect, useState } from 'react';

interface IscedCode {
  code: string;
  label_no: string;
  label_en: string;
  forklaring_no: string | null;
}

interface IscedGroup {
  broad_field: string;
  broad_label: string;
  codes: IscedCode[];
}

interface IscedFSelectProps {
  value: string | null;
  onChange: (code: string | null) => void;
  className?: string;
}

const CACHE_KEY = 'isced_f_codes_v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 timer

export default function IscedFSelect({ value, onChange, className }: IscedFSelectProps) {
  const [groups, setGroups] = useState<IscedGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadCodes() {
      try {
        // Sjekk sessionStorage-cache
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { groups: cachedGroups, fetchedAt } = JSON.parse(cached);
            if (Date.now() - fetchedAt < CACHE_TTL_MS && Array.isArray(cachedGroups)) {
              if (!cancelled) {
                setGroups(cachedGroups);
                setLoading(false);
                return;
              }
            }
          } catch { /* ignorer parse-feil, hent på nytt */ }
        }

        const res = await fetch('/api/isced-f/codes');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (!data.success || !Array.isArray(data.groups)) {
          throw new Error('Ugyldig respons fra server');
        }

        if (!cancelled) {
          setGroups(data.groups);
          setLoading(false);
          try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify({
              groups: data.groups,
              fetchedAt: Date.now()
            }));
          } catch { /* sessionStorage full eller deaktivert */ }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lasting feilet');
          setLoading(false);
        }
      }
    }

    loadCodes();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <select className={className || 'input'} disabled value="">
        <option>Laster ISCED-F-koder…</option>
      </select>
    );
  }

  if (error) {
    return (
      <select className={className || 'input'} disabled value="">
        <option>Kunne ikke laste koder: {error}</option>
      </select>
    );
  }

  return (
    <select
      className={className || 'input'}
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="">Velg fagområde…</option>
      {groups.map(group => (
        <optgroup key={group.broad_field} label={`${group.broad_field} — ${group.broad_label}`}>
          {group.codes.map(c => (
            <option
              key={c.code}
              value={c.code}
              title={c.forklaring_no || c.label_en}
            >
              {c.code} — {c.label_no || c.label_en}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
